import { AppOptimizelyProvider } from './AppOptimizelyProvider'
import { AtlasProvider } from '@dtx-company/inter-app/src/hoc/atlas-provider/provider'
import { FC, ReactNode, useState } from 'react'
import { FlowSWRConfig } from '@dtx-company/inter-app/src/components/FlowSWRConfig'
import { HooksRequiringProviders } from './HooksRequiringProviders'
import { ImpactSync } from './Impact'
import {
  InternalAssetsContext,
  initialInternalAssetCounts
} from '../../../hooks/useInternalAssetsData'
import { PersistGate } from 'redux-persist/integration/react'
import { SessionAndOptimizelyValues } from '../types'
import { StyleSheetManager } from 'styled-components'
import { XStateProvider } from './XStateProvider'
import { persistor } from '@dtx-company/inter-app/src/redux/persistor'
import { useOnce } from '@dtx-company/true-common/src/hooks/useOnce'
import isPropValid from '@emotion/is-prop-valid'

export type PageProvidersProps = {
  swrFallback?: Record<string, unknown>
} & SessionAndOptimizelyValues

export const PageProviders: FC<PageProvidersProps & { children: ReactNode }> = ({
  children,
  swrFallback,
  session,
  optimizely,
  ...pageProps
}) => {
  const [orgAssetCounts, setOrgAssetCounts] = useState(initialInternalAssetCounts)

  /*
  the internalAssetsClient connects to a staging service running in-cluster, to access:
  1. set up your zsh client (not bash) following the steps in @dtx-company/sre-scripts
  2. aws-profile fc-services-stg:fc-eng-backend-engineer
  3. aws sso login
  4. kubectl --context='stg-use1-services-126 - fc-eng-backend-engineer - fc-services-stg - use1' port-forward svc/fc-backend-assets-service-fc-service -n fc-backend-assets-service 50051:80
*/

  useOnce(() => {
    const effect = async (): Promise<void> => {
      const res = await fetch('/api/internal-assets', {
        method: 'POST'
      })

      const { codeCount, pageCount } = await res.json()

      setOrgAssetCounts({ codeCount, pageCount })
    }
    effect()
  })

  return (
    <InternalAssetsContext.Provider value={orgAssetCounts}>
      <StyleSheetManager
        enableVendorPrefixes
        shouldForwardProp={(propName, elementToBeRendered) => {
          return typeof elementToBeRendered === 'string' ? isPropValid(propName) : true
        }}
        // https://github.com/styled-components/styled-components/issues/4071#issuecomment-1625465480
        {...pageProps}
      >
        <FlowSWRConfig fallback={swrFallback}>
          <AppOptimizelyProvider session={session} optimizely={optimizely}>
            <AtlasProvider>
              <HooksRequiringProviders session={session} />
              <XStateProvider
                isMobile={optimizely?.attributes?.deviceType !== 'desktop'}
                {...pageProps}
              >
                <PersistGate loading={children} persistor={persistor}>
                  {children}
                  <ImpactSync />
                </PersistGate>
              </XStateProvider>
            </AtlasProvider>
          </AppOptimizelyProvider>
        </FlowSWRConfig>
      </StyleSheetManager>
    </InternalAssetsContext.Provider>
  )
}
